import { Grid } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';
import React, { useEffect, useState, Dispatch, FunctionComponent } from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import Slider, { Settings } from 'react-slick';
import compose from 'recompose/compose';
import { Action } from 'redux';
import {
  SaviSelectIntlAirSegmentDetail,
  SaviSelectIntlCombination,
  SelectedCombinations,
} from '../../models/itinerary';
import { AppState } from '../../store/model';
import { setRecommandedCombination } from '../../store/reducers/itinerary/actions';
import { shouldShowPricing } from '../../store/reducers/itinerary/selectors';
import { spacings } from '../lab/spacings';
import { AirTotalPrice } from './AirTotalPrice';
import { CombinationRoute } from './CombinationRoute';
import { CombinationRouteDetail } from './CombinationRouteDetail';
import { SelectButton } from './SelectButton';
import { TableAirTitle } from './TableAirTitle';
import {
  getAirSegmentDetailsForSaviSelectIntl,
  getFlightCombinationsForSaviSelectIntl,
  getSelectedCombinationsForSaviSelectIntl,
} from '../../store/reducers/itinerary/saviSelectIntlSelector';

interface StateProps {
  combinationFlight?: SaviSelectIntlCombination[];
  airSegmentDetails: SaviSelectIntlAirSegmentDetail[];
  showTotalPrice: boolean;
  selectedCombinations: SelectedCombinations;
}

interface DispatchProps {
  setAirInitRecommended(combinationIndex: number): void;
}

interface CombinationGroupsProps
  extends StateProps,
  DispatchProps,
  WithStyles<typeof styles>,
  InjectedIntlProps { }

const _CombinationGroupsBase: FunctionComponent<CombinationGroupsProps> = ({
  classes,
  combinationFlight,
  airSegmentDetails,
  showTotalPrice,
  selectedCombinations,
  setAirInitRecommended,
  intl: { formatNumber },
}) => {
  const [beforeCurrentSlide, setBeforeCurrentSlide] = useState<number>(0);
  const [beforeNextSlide, setBeforeNextSlide] = useState<number>(0);
  const [afterCurrentSlide, setAfterCurrentSlide] = useState<number>(0);
  const [sliderCss, setSliderCss] = useState<any>(classes.slider);
  const [routeHtml, setRouteHtml] = useState<any[]>([]);
  const [routeDetailHtml, setRouteDetailHtml] = useState<any[]>([]);

  const getSliderButton = (icon: JSX.Element) => {
    return (
      <div>
        <div className={classes.slickArrowClick}>
          <span>{icon}</span>
        </div>
      </div>
    );
  };

  const sliderSettings: Partial<Settings> = {
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    nextArrow: getSliderButton(<ChevronRight />),
    prevArrow: getSliderButton(<ChevronLeft />),
    afterChange: (currentSlide: number) => {
      setAfterCurrentSlide(currentSlide);
    },
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setBeforeCurrentSlide(currentSlide);
      setBeforeNextSlide(nextSlide);
    },
  };

  useEffect(() => {
    if (
      combinationFlight &&
      combinationFlight.length > 0 &&
      airSegmentDetails &&
      airSegmentDetails.length > 0
    ) {
      let newHtml: any[] = [];

      if (
        !selectedCombinations &&
        combinationFlight &&
        combinationFlight.length > 0
      ) {
        setAirInitRecommended(combinationFlight[0].index);
      }

      const currentSelectIndex = selectedCombinations
        ? selectedCombinations.selectedAirCombinationIndex
        : 0;
      combinationFlight.forEach((item, i) => {
        const isFirstFlight = i === 0;
        const isLastFlight = i === combinationFlight.length - 1;
        if (i === 0) {
          item.segments.forEach((itemRoute, index) => {
            const isLastRoute =
              index === item.segments.length - 1 && !showTotalPrice;
            const airDetail = airSegmentDetails.find(
              (detailItem) =>
                detailItem.segmentNo === itemRoute.segmentNo &&
                detailItem.flightId === itemRoute.id,
            );
            if (airDetail) {
              newHtml = [
                ...newHtml,
                <CombinationRoute
                  isLastRoute={isLastRoute}
                  fromCity={airDetail.fromCity}
                  toCity={airDetail.toCity}
                  key={index}
                />,
              ];
            }
          });
          setRouteHtml(newHtml);
          newHtml = [];
        }

        let nodeHtml: any[] = [];
        let totalPrice = '';
        let currencyCode = '';
        item.segments.map((segment) => {
          const airDetail = airSegmentDetails.find(
            (detailItem) =>
              detailItem.segmentNo === segment.segmentNo &&
              detailItem.flightId === segment.id &&
              detailItem.fareInfo.id === segment.fareId,
          );

          currencyCode = airDetail ? airDetail.fareInfo.currencyCode : '';
          totalPrice = formatNumber(
            airDetail ? airDetail.fareInfo.totalCost : 0,
            {
              style: 'currency',
              currency: currencyCode,
              currencyDisplay: 'narrowSymbol',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            },
          );

          nodeHtml = [
            ...nodeHtml,
            <CombinationRouteDetail
              isSelected={i === currentSelectIndex}
              airDetail={airDetail}
              key={`${segment.segmentNo}_${segment.id}`}
              isLastFlight={isLastFlight}
            />,
          ];
        });

        newHtml = [
          ...newHtml,
          <div key={i}>
            <TableAirTitle
              key={i}
              isSelected={i === currentSelectIndex}
              isRecommended={i === 0}
              isFirstAirTitle={isFirstFlight}
            />
            {nodeHtml}
            {showTotalPrice && (
              <AirTotalPrice
                priceText={`${totalPrice} ${currencyCode}`}
                key={`AirTotalPrice_${i}`}
                isSelected={i === currentSelectIndex}
                isLastFlight={isLastFlight}
              />
            )}
            <SelectButton
              combinationIndex={item.index}
              isSelected={i === currentSelectIndex}
              key={`SelectButton_${i}`}
              isLastFlight={isLastFlight}
            />
          </div>,
        ];
      });
      setRouteDetailHtml(newHtml);
    }
  }, [
    combinationFlight,
    airSegmentDetails,
    selectedCombinations ? selectedCombinations.selectedAirCombinationIndex : 0,
  ]);

  useEffect(() => {
    const firstNodeInLastSlide =
      combinationFlight && combinationFlight.length > 0
        ? combinationFlight.length - 3
        : 0;
    if (afterCurrentSlide === 0) {
      setSliderCss(classes.sliderShowRightBar);
    } else if (afterCurrentSlide === firstNodeInLastSlide) {
      setSliderCss(classes.sliderShowLeftBar);
    } else {
      setSliderCss(classes.slider);
    }
  }, [beforeCurrentSlide, beforeNextSlide, afterCurrentSlide]);

  return (
    <div className={classes.combinationWarp}>
      <div className={classes.conbinationsRowWarp}>
        <Grid className={classes.combinationButtonEmpty}>
          <div className={classes.combinationTitleEmpty} />
          {routeHtml}
          {showTotalPrice && (
            <Grid className={classes.totalPriceWarp}>
              <Typography className={classes.totalPrictText}>
                Total price
              </Typography>
            </Grid>
          )}
        </Grid>
        <Grid style={{ width: '699px' }}>
          <Slider {...sliderSettings} className={sliderCss}>
            {routeDetailHtml}
          </Slider>
        </Grid>
      </div>
    </div>
  );
};

const mapStateToProps = (state: AppState): StateProps => {
  return {
    combinationFlight: getFlightCombinationsForSaviSelectIntl(state),
    airSegmentDetails: getAirSegmentDetailsForSaviSelectIntl(state),
    showTotalPrice: shouldShowPricing(state),
    selectedCombinations: getSelectedCombinationsForSaviSelectIntl(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  setAirInitRecommended: (combinationIndex: number) => {
    dispatch(
      setRecommandedCombination({
        name: 'selectedAirCombinationIndex',
        index: combinationIndex,
      }),
    );
  },
});

const styles = (theme: Theme) => {
  const {
    between: {
      card: { header: cardHeaderSpacing },
    },
  } = spacings(theme);

  return createStyles({
    combinationTitleWarp: {
      display: 'inline-flex',
    },
    combinationTitleEmpty: {
      width: '140px',
      height: '35px',
    },
    combinationButtonEmpty: {
      width: '140px',
    },
    combinationButton: {
      display: 'inline-flex',
      borderTop: '1px rgba(127, 127, 127, 0.50) solid',
    },
    combinationWarp: {
      ...cardHeaderSpacing,
    },
    combinationWrapBorder: {
      borderRight: '1px #E0E0E0 solid',
    },
    conbinationsRowWarp: {
      display: 'inline-flex',
    },
    totalPriceWarp: {
      width: '140px',
      height: '51px',
      padding: theme.spacing.unit * 2,
      background: 'white',
      borderLeft: '1px #E0E0E0 solid',
      borderTop: '1px #E0E0E0 solid',
      borderBottom: '1px #E0E0E0 solid',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      display: 'inline-flex',
      boxSizing: 'border-box',
    },
    totalPriceTextWarp: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      display: 'flex',
    },
    totalPrictText: {
      textAlign: 'center',
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: 16,
      fontFamily: 'Roboto Medium',
      fontWeight: 500,
      letterSpacing: 0.15,
      wordWrap: 'break-word',
    },
    slider: {
      '& .slick-slide': {
        flex: 'none',
      },
      '& .slick-track': {
        marginLeft: 0,
      },
      '& .slick-prev:before': {
        content: 'none',
      },
      '& .slick-next:before': {
        content: 'none',
      },
      '& .slick-arrow': {
        width: theme.spacing.unit * 3,
        height: '100%',
        backgroundColor: theme.palette.action.hover,
        color: 'black',
        zIndex: 9,
        alignContent: 'center',
        transition: 'opacity 0.3s ease',
        border: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .slick-next': {
        right: '0px',
      },
      '& .slick-prev': {
        left: '0px',
      },
    },
    sliderShowLeftBar: {
      '& .slick-slide': {
        flex: 'none',
      },
      '& .slick-track': {
        marginLeft: 0,
      },
      '& .slick-prev:before': {
        content: 'none',
      },
      '& .slick-next:before': {
        content: 'none',
      },
      '& .slick-arrow': {
        width: theme.spacing.unit * 3,
        height: '100%',
        backgroundColor: theme.palette.action.hover,
        color: 'black',
        zIndex: 9,
        alignContent: 'center',
        transition: 'opacity 0.3s ease',
        border: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .slick-next': {
        right: '0px',
        visibility: 'hidden',
      },
      '& .slick-prev': {
        left: '0px',
      },
    },
    sliderShowRightBar: {
      '& .slick-slide': {
        flex: 'none',
      },
      '& .slick-track': {
        marginLeft: 0,
      },
      '& .slick-prev:before': {
        content: 'none',
      },
      '& .slick-next:before': {
        content: 'none',
      },
      '& .slick-arrow': {
        width: theme.spacing.unit * 3,
        height: '100%',
        backgroundColor: theme.palette.action.hover,
        color: 'black',
        zIndex: 9,
        alignContent: 'center',
        transition: 'opacity 0.3s ease',
        border: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .slick-next': {
        right: '0px',
      },
      '& .slick-prev': {
        left: '0px',
        visibility: 'hidden',
      },
    },
    sliderNoShowBar: {
      '& .slick-slide': {
        flex: 'none',
      },
      '& .slick-track': {
        marginLeft: 0,
      },
      '& .slick-prev:before': {
        content: 'none',
      },
      '& .slick-next:before': {
        content: 'none',
      },
      '& .slick-arrow': {
        width: theme.spacing.unit * 3,
        height: '100%',
        backgroundColor: theme.palette.action.hover,
        color: 'black',
        zIndex: 9,
        alignContent: 'center',
        transition: 'opacity 0.3s ease',
        border: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .slick-next': {
        right: '0px',
        visibility: 'visible',
      },
      '& .slick-prev': {
        left: '0px',
        visibility: 'visible',
      },
    },
    slickArrowClick: {
      height: '100%',
      alignContent: 'center',
    },
  });
};

const enhance = compose<CombinationGroupsProps, {}>(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  injectIntl,
);

export const CombinationGroups = enhance(_CombinationGroupsBase);

CombinationGroups.displayName = 'CombinationGroups';
